import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Upload, FileUp, CheckCircle, AlertCircle, Download, Database, Package } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const DataUploadRES = () => {
  const { user } = useAuth();
  const [selectedDataType, setSelectedDataType] = useState('');
  const [file, setFile] = useState(null);
  const [uploadMode, setUploadMode] = useState('auto');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  // RES_* Data types (Phase 1 + Phase 2)
  const resDataTypes = {
    phase1Masters: [
      { 
        value: 'res_store', 
        label: 'RES_STORE', 
        description: 'Master Store Data - Store hierarchy, geography, attributes',
        endpoint: '/api/res-upload-v2/store',
        templateEndpoint: '/api/res-upload/templates/store',
        icon: <Database className="h-5 w-5" />,
        phase: 'Phase 1'
      },
      { 
        value: 'res_product', 
        label: 'RES_PRODUCT', 
        description: 'Master Product Data - SKU details, hierarchies, brand information',
        endpoint: '/api/res-upload-v2/product',
        templateEndpoint: '/api/res-upload/templates/product',
        icon: <Package className="h-5 w-5" />,
        phase: 'Phase 1'
      },
      { 
        value: 'res_vendor', 
        label: 'RES_VENDOR', 
        description: 'Master Vendor Data - Supplier information and hierarchy',
        endpoint: '/api/res-upload-v2/vendor',
        templateEndpoint: '/api/res-upload/templates/vendor',
        icon: <Database className="h-5 w-5" />,
        phase: 'Phase 1'
      },
    ],
    phase2Masters: [
      { 
        value: 'res_customer', 
        label: 'RES_CUSTOMER', 
        description: 'Master Customer Data - Demographics, loyalty, contact information',
        endpoint: '/api/res-upload-v2/customer',
        templateEndpoint: '/api/res-upload/templates/customer',
        icon: <Database className="h-5 w-5" />,
        phase: 'Phase 2'
      },
      { 
        value: 'res_return_reason', 
        label: 'RES_RETURN_REASON', 
        description: 'Master Return Reason Codes - Return types and reasons',
        endpoint: '/api/res-upload-v2/return-reason',
        templateEndpoint: '/api/res-upload/templates/return-reason',
        icon: <Database className="h-5 w-5" />,
        phase: 'Phase 2'
      },
      { 
        value: 'res_discount_details', 
        label: 'RES_DISCOUNT_DETAILS', 
        description: 'Master Discount Details - Discount types and reason codes',
        endpoint: '/api/res-upload-v2/discount-details',
        templateEndpoint: '/api/res-upload/templates/discount-details',
        icon: <Database className="h-5 w-5" />,
        phase: 'Phase 2'
      },
      { 
        value: 'res_promotions', 
        label: 'RES_PROMOTIONS', 
        description: 'Master Promotions - Campaign dates, budgets, and targets',
        endpoint: '/api/res-upload-v2/promotions',
        templateEndpoint: '/api/res-upload/templates/promotions',
        icon: <Database className="h-5 w-5" />,
        phase: 'Phase 2'
      },
    ],
    transactions: [
      { 
        value: 'res_sales', 
        label: 'RES_SALES', 
        description: 'Transaction Sales Data - Daily sales at SKU × Store × Date level',
        endpoint: '/api/res-upload-v2/sales',
        templateEndpoint: '/api/res-upload/templates/sales',
        icon: <FileUp className="h-5 w-5" />,
        phase: 'Phase 1'
      },
      { 
        value: 'res_inventory', 
        label: 'RES_INVENTORY_POSITION', 
        description: 'Transaction Inventory Data - Stock snapshots at SKU × Store × Date',
        endpoint: '/api/res-upload-v2/inventory',
        templateEndpoint: '/api/res-upload/templates/inventory',
        icon: <FileUp className="h-5 w-5" />,
        phase: 'Phase 1'
      },
      { 
        value: 'res_footfall', 
        label: 'RES_FOOTFALL', 
        description: 'Transaction Footfall Data - Daily store visitor counts',
        endpoint: '/api/res-upload-v2/footfall',
        templateEndpoint: '/api/res-upload/templates/footfall',
        icon: <FileUp className="h-5 w-5" />,
        phase: 'Phase 2'
      },
      { 
        value: 'res_plan', 
        label: 'RES_PLAN', 
        description: 'Transaction Plan Data - Forecast and budget planning data',
        endpoint: '/api/res-upload-v2/plan',
        templateEndpoint: '/api/res-upload/templates/plan',
        icon: <FileUp className="h-5 w-5" />,
        phase: 'Phase 3'
      },
      { 
        value: 'res_target', 
        label: 'RES_TARGET', 
        description: 'Transaction Target Data - Performance targets and KPIs',
        endpoint: '/api/res-upload-v2/target',
        templateEndpoint: '/api/res-upload/templates/target',
        icon: <FileUp className="h-5 w-5" />,
        phase: 'Phase 3'
      },
    ]
  };

  const handleFileChange = (e) => {
    if (!selectedDataType) {
      setError('Please select a Data Type first');
      e.target.value = '';
      return;
    }
    
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = ['.csv', '.xlsx', '.xls'];
      const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
      
      if (validTypes.includes(fileExtension)) {
        setFile(selectedFile);
        setError(null);
      } else {
        setError('Please select a valid CSV or Excel file');
        setFile(null);
        e.target.value = '';
      }
    }
  };
  
  const handleRemoveFile = () => {
    setFile(null);
    setError(null);
    const fileInput = document.getElementById('file-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };
  
  const handleDataTypeChange = (newType) => {
    setSelectedDataType(newType);
    if (file) {
      handleRemoveFile();
    }
    setUploadResult(null);
    setError(null);
  };

  const getDataTypeConfig = (type) => {
    const allTypes = [
      ...resDataTypes.phase1Masters, 
      ...resDataTypes.phase2Masters, 
      ...resDataTypes.transactions
    ];
    return allTypes.find(dt => dt.value === type);
  };

  // Poll job status
  const pollJobStatus = async (jobId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/upload-jobs/status/${jobId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      setJobStatus(response.data);

      // If job is complete, stop polling
      if (response.data.status === 'SUCCESS' || response.data.status === 'FAILED') {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
        setUploading(false);
        
        // Set final result
        setUploadResult({
          success: response.data.status === 'SUCCESS',
          message: response.data.status === 'SUCCESS' 
            ? `Successfully uploaded ${response.data.insertedRows} records`
            : response.data.error?.message || 'Upload failed',
          totalRows: response.data.totalRows,
          insertedRows: response.data.insertedRows,
          failedRows: response.data.failedRows,
          status: response.data.status,
          error: response.data.error
        });
      }
    } catch (error) {
      console.error('Error polling job status:', error);
    }
  };

  // Cleanup polling on unmount
  React.useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const handleDownloadTemplate = async (dataType) => {
    try {
      const config = getDataTypeConfig(dataType);
      if (!config) return;

      const response = await axios.get(
        `${BACKEND_URL}${config.templateEndpoint}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `${dataType}_template.xlsx`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      setError('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!selectedDataType || !file) {
      setError('Please select both data type and file');
      return;
    }

    const config = getDataTypeConfig(selectedDataType);
    if (!config) {
      setError('Invalid data type selected');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_mode', uploadMode);
      
      // For transaction tables, add duplicate check
      if (resDataTypes.transactions.find(t => t.value === selectedDataType)) {
        formData.append('check_duplicates', 'true');
      }

      const response = await axios.post(
        `${BACKEND_URL}${config.endpoint}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // Check if response has jobId (new async pattern)
      if (response.data.jobId) {
        setCurrentJobId(response.data.jobId);
        setJobStatus({ status: 'PENDING', message: response.data.message });
        
        // Start polling for job status every 2 seconds
        const interval = setInterval(() => {
          pollJobStatus(response.data.jobId);
        }, 2000);
        setPollingInterval(interval);
        
        // Initial poll
        pollJobStatus(response.data.jobId);
      } else {
        // Old synchronous response
        setUploadResult(response.data);
        setUploading(false);
        if (response.data.success) {
          handleRemoveFile();
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.response?.data?.detail || 'Upload failed. Please try again.');
      setUploading(false);
    }
  };

  const isTransactionTable = selectedDataType && resDataTypes.transactions.find(t => t.value === selectedDataType);
  const isMasterTable = selectedDataType && (
    resDataTypes.phase1Masters.find(t => t.value === selectedDataType) || 
    resDataTypes.phase2Masters.find(t => t.value === selectedDataType)
  );
  const isAdmin = user?.role === 'superadmin' || user?.role === 'companyadmin';

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Data Upload</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-2">
          Upload data to the canonical RES_* schema (Phase 1 + Phase 2 + Phase 3)
        </p>
      </div>

      {/* Upload Instructions */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardHeader>
          <CardTitle className="text-blue-900 dark:text-blue-100 flex items-center gap-2">
            <Database className="h-5 w-5" />
            Upload Guidelines
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
            <p><strong>Master Tables (Store, Product, Vendor):</strong></p>
            <ul className="list-disc ml-6 space-y-1">
              <li><strong>Auto Mode:</strong> Append if data exists, full load if empty (default for normal users)</li>
              <li><strong>Replace Mode:</strong> Truncate and reload entire table (admin only)</li>
            </ul>
            <p className="mt-3"><strong>Transaction Tables (Sales, Inventory):</strong></p>
            <ul className="list-disc ml-6 space-y-1">
              <li><strong>Append Mode:</strong> Add new records with duplicate check (default for daily loads)</li>
              <li><strong>Replace Full:</strong> Truncate and reload all data (admin only)</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Phase 1 Master Tables Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-6 w-6" />
            Phase 1 Master Tables
          </CardTitle>
          <CardDescription>
            Core reference data - Store, Product, and Vendor information
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {resDataTypes.phase1Masters.map((dataType) => (
              <div
                key={dataType.value}
                onClick={() => handleDataTypeChange(dataType.value)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedDataType === dataType.value
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="text-blue-600 dark:text-blue-400 mt-1">
                    {dataType.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 dark:text-white">
                      {dataType.label}
                    </h3>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                      {dataType.description}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadTemplate(dataType.value);
                      }}
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Template
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>


      {/* Phase 2 Master Tables Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-6 w-6" />
            Phase 2 Master Tables
          </CardTitle>
          <CardDescription>
            Extended reference data - Customer, Promotions, Returns, Discounts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {resDataTypes.phase2Masters.map((dataType) => (
              <div
                key={dataType.value}
                onClick={() => handleDataTypeChange(dataType.value)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedDataType === dataType.value
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-700'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="text-purple-600 dark:text-purple-400 mt-1">
                    {dataType.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 dark:text-white">
                      {dataType.label}
                    </h3>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                      {dataType.description}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadTemplate(dataType.value);
                      }}
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Template
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Transaction Tables Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileUp className="h-6 w-6" />
            Transaction Tables
          </CardTitle>
          <CardDescription>
            Transactional data - Sales, Inventory, Footfall, Plan, and Target data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {resDataTypes.transactions.map((dataType) => (
              <div
                key={dataType.value}
                onClick={() => handleDataTypeChange(dataType.value)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedDataType === dataType.value
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                    : 'border-slate-200 dark:border-slate-700 hover:border-green-300 dark:hover:border-green-700'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="text-green-600 dark:text-green-400 mt-1">
                    {dataType.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 dark:text-white">
                      {dataType.label}
                    </h3>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                      {dataType.description}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadTemplate(dataType.value);
                      }}
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Template
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Upload Configuration Card */}
      {selectedDataType && (
        <Card>
          <CardHeader>
            <CardTitle>Upload Configuration</CardTitle>
            <CardDescription>
              Selected: {getDataTypeConfig(selectedDataType)?.label}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Upload Mode Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Upload Mode
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div
                  onClick={() => setUploadMode('auto')}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    uploadMode === 'auto'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-slate-200 dark:border-slate-700'
                  } ${!isMasterTable ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="font-medium text-sm">Auto (Recommended)</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                    Smart mode: Append or full load based on data
                  </div>
                </div>
                
                <div
                  onClick={() => isMasterTable && setUploadMode('append')}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    uploadMode === 'append'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-slate-200 dark:border-slate-700'
                  } ${!isMasterTable ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="font-medium text-sm">Append</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                    Add new records only
                  </div>
                </div>
                
                <div
                  onClick={() => isAdmin && setUploadMode(isTransactionTable ? 'replace_full' : 'replace')}
                  className={`p-3 border-2 rounded-lg transition-all ${
                    uploadMode === 'replace' || uploadMode === 'replace_full'
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                      : 'border-slate-200 dark:border-slate-700'
                  } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="font-medium text-sm">Replace {isAdmin ? '' : '(Admin Only)'}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                    Truncate and reload all data
                  </div>
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Select File (CSV or Excel)
              </label>
              <div className="flex items-center gap-3">
                <input
                  id="file-input"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/20 dark:file:text-blue-400"
                />
                {file && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRemoveFile}
                  >
                    Remove
                  </Button>
                )}
              </div>
              {file && (
                <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                  Selected: {file.name}
                </p>
              )}
            </div>

            {/* Upload Button */}
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="w-full"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Data
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-900 dark:text-red-100">Upload Error</h3>
                <p className="text-sm text-red-800 dark:text-red-200 mt-1">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Job Progress Display */}
      {jobStatus && (jobStatus.status === 'PENDING' || jobStatus.status === 'PROCESSING') && (
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100">
                  {jobStatus.status === 'PENDING' ? 'Upload Starting...' : 'Processing Upload...'}
                </h3>
                <div className="text-sm text-blue-800 dark:text-blue-200 mt-2 space-y-2">
                  {jobStatus.totalRows > 0 && (
                    <>
                      <p><strong>Progress:</strong> {jobStatus.processedRows || 0} / {jobStatus.totalRows} rows</p>
                      {jobStatus.progressPct !== undefined && (
                        <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${jobStatus.progressPct}%` }}
                          />
                        </div>
                      )}
                      <p className="text-xs">
                        Job ID: {currentJobId}
                      </p>
                    </>
                  )}
                  {jobStatus.message && <p>{jobStatus.message}</p>}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Success Display */}
      {uploadResult && uploadResult.success && (
        <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-green-900 dark:text-green-100">Upload Successful</h3>
                <div className="text-sm text-green-800 dark:text-green-200 mt-2 space-y-1">
                  <p><strong>Total Rows:</strong> {uploadResult.totalRows}</p>
                  <p><strong>Successful:</strong> {uploadResult.successfulRows}</p>
                  <p><strong>Failed:</strong> {uploadResult.failedRows}</p>
                  {uploadResult.duplicateRows > 0 && (
                    <p><strong>Duplicates Skipped:</strong> {uploadResult.duplicateRows}</p>
                  )}
                  <p><strong>Upload Mode:</strong> {uploadResult.uploadMode}</p>
                </div>
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div className="mt-3">
                    <p className="font-semibold">Errors (first 10):</p>
                    <ul className="list-disc ml-5 mt-1 text-xs space-y-1">
                      {uploadResult.errors.slice(0, 10).map((err, idx) => (
                        <li key={idx}>Row {err.row}: {err.errors.join(', ')}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DataUploadRES;
